import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { getInbox, type InboxItem } from '../api'
import { dateTime, relativeDays } from '../format'
import { endedWord, recipientBadge } from './EnvelopeDetail'

/**
 * The person's own part on the row. On a document that ended without
 * everyone (withdrawn, or declined by somebody else) nobody waits on them any
 * more, so "pending" is false: it reads "withdrawn" or "closed", and
 * "viewed" keeps its word, both in no state's ink, by the rule the envelope
 * page's Recipients card uses (recipientBadge; review 6, app request 2).
 * Otherwise their status, in its own ink. The app's myPart (24da424) is this
 * rule.
 */
export function myPart(e: Pick<InboxItem, 'status' | 'my_status'>): { ink: string; label: string } {
  if (endedWord(e.status) !== null && e.my_status !== 'signed' && e.my_status !== 'declined') return recipientBadge(e.my_status, e.status)
  return { ink: e.my_status, label: e.my_status }
}

/**
 * What was sent to you.
 *
 * The envelope list answers "what can I see", which is keyed on the
 * organization and mixes a company's outgoing documents with the ones its
 * staff received. That left no answer at all to the question a recipient
 * actually has, so the only way to find a document somebody sent you was to
 * search a list of your colleagues' sends for a row tagged "you are a signer".
 *
 * The count this page leads with is an OBLIGATION, not an unread marker. A
 * document you have opened and not signed still needs you; one you signed an
 * hour ago does not, even though the envelope is still open on somebody else.
 * Measuring attention instead would put a badge on things that want nothing.
 */
export function Inbox() {
  // null while we are still asking, so an empty state does not flash in front
  // of somebody who has post.
  const [items, setItems] = useState<InboxItem[] | null>(null)
  const [waiting, setWaiting] = useState(0)
  const [error, setError] = useState('')

  useEffect(() => {
    getInbox()
      .then((r) => {
        setItems(r.envelopes ?? [])
        setWaiting(r.waiting_on_you ?? 0)
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not read your inbox.'))
  }, [])

  return (
    <main className="page">
      <div className="page-head">
        <h1>Sent to you</h1>
        <p>
          {items === null
            ? 'Documents other people asked you to sign.'
            : waiting > 0
              ? `${waiting} ${waiting === 1 ? 'document needs' : 'documents need'} you now.`
              : 'Documents other people asked you to sign. Nothing needs you right now.'}
        </p>
        {/* Said plainly rather than implied. Signing happens through the link
            that was emailed to you, because that link is the credential the
            signature is anchored to — opening a ceremony from a session
            instead would be a different way of proving who signed, and that is
            a decision about evidence rather than a convenience. Until it is
            taken, a row here opens the document and its trail, and the email
            is still where you sign. */}
        {items !== null && items.length > 0 && (
          <p className="small faint">
            Open a document to read it and see its trail. Signing is done from the link
            emailed to you.
          </p>
        )}
      </div>

      {error && (
        <div className="notice notice-bad" role="alert">
          {error}
        </div>
      )}

      {items === null && <p className="muted">Loading…</p>}

      {items !== null && items.length === 0 && !error && (
        <div className="card empty">
          <h2>Nothing has been sent to you</h2>
          <p>
            When somebody asks you to sign a document, it appears here. Documents your
            organization sends are under Envelopes.
          </p>
          <Link className="btn" to="/">
            Your organization&rsquo;s documents
          </Link>
        </div>
      )}

      {items !== null && items.length > 0 && (
        <div className="table-wrap">
          <table className="table inbox-table">
            <thead>
              <tr>
                <th>Document</th>
                <th className="drops-narrow">From</th>
                <th>Your part</th>
                <th className="drops-narrow">Sent</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id}>
                  <td>
                    <Link to={`/envelopes/${e.id}`}>{e.title}</Link>
                    {e.waiting_on_me && (
                      <>
                        {' '}
                        <span className="badge badge-sent">needs you</span>
                      </>
                    )}
                  </td>
                  <td className="muted drops-narrow">{e.sent_by_organization || '—'}</td>
                  <td>
                    <span className={`badge badge-${myPart(e).ink}`}>{myPart(e).label}</span>
                    <span className="muted small"> · {e.role}</span>
                  </td>
                  <td className="muted small drops-narrow" title={dateTime(e.created_at)}>
                    {relativeDays(e.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}
